// Tooltip — the supplementary-hint primitive, pure. A thin wrapper over
// @radix-ui/react-tooltip.
//
// Rebuilt from foundations v1 against the design vision (.claude/research/design/06).
// A tooltip is a small plane that genuinely floats above the page, so it
// wears the border→elevation discipline (§5): no resting edge, just the
// elevated ground + a soft shadow (--shadow-md, the popover level). Text
// is --text-xs — it is a whispered aside, never a heading.
//
// SUPPLEMENTARY ONLY: a tooltip is hover/focus-gated and vanishes, so it
// must never be the ONLY place a critical fact lives (a required rule, a
// destructive consequence, an error). Anything load-bearing goes in the
// always-visible layer; the tooltip only enriches. Colour is not a channel
// here at all — it is plain ink on the elevated plane.
//
// Pure/props-driven: `content` is the hint, `children` is the trigger
// (rendered `asChild`, so the caller's own focusable element BECOMES the
// trigger and keeps its semantics). `side` and `delayDuration` tune
// placement/timing. All hover/focus/keyboard/dismiss/positioning behaviour
// comes from Radix — we only paint. No state, no side effects.
//
// Radix requires ONE <TooltipProvider> mounted around the app (it shares the
// open-delay across tooltips so moving between them feels instant). Export it
// here; the app's root mounts it once. `side` is a named union, not a loose
// string, so placement is typed on the public surface.
//
// Composable as ANOTHER Radix trigger's child (e.g. a Popover trigger that
// also wants a hint): the outer trigger's `asChild` Slot hands this component
// its ref + trigger props (onClick, aria-expanded, …), which are forwarded to
// the inner Trigger and so reach the button. Without this, the outer props
// would be silently dropped here.

import * as RadixTooltip from "@radix-ui/react-tooltip";
import {
    type ComponentPropsWithoutRef,
    forwardRef,
    type ReactNode,
} from "react";
import "./Tooltip.css";

export type TooltipSide = "top" | "right" | "bottom" | "left";

// Trigger props a composing outer Radix trigger may inject (see the header).
// `content` is omitted: the HTML attribute of that name would collide with
// the hint.
type ForwardedTriggerProps = Omit<
    ComponentPropsWithoutRef<typeof RadixTooltip.Trigger>,
    "content" | "children"
>;

export type TooltipProps = ForwardedTriggerProps & {
    // The supplementary hint. Kept short — this is an aside, not a paragraph.
    readonly content: ReactNode;
    // The trigger element. Rendered `asChild`: pass a single focusable node
    // (a Button, an IconButton…) and it becomes the tooltip's anchor.
    readonly children: ReactNode;
    readonly side?: TooltipSide;
    // Hover dwell before the hint appears, in ms. Defaults to a short delay
    // so a deliberate hover reveals it quickly without firing on pass-through.
    readonly delayDuration?: number;
};

// The gap (px) between the trigger and the tip, sized to clear the arrow.
// A layout constant of the pointer geometry, not a themable surface value.
const SIDE_OFFSET = 6;

export const Tooltip = forwardRef<HTMLButtonElement, TooltipProps>(
    (
        { content, children, side = "top", delayDuration = 300, ...trigger },
        ref,
    ) => (
        <RadixTooltip.Root delayDuration={delayDuration}>
            <RadixTooltip.Trigger {...trigger} asChild ref={ref}>
                {children}
            </RadixTooltip.Trigger>
            <RadixTooltip.Portal>
                <RadixTooltip.Content
                    className="bilda-tooltip"
                    side={side}
                    sideOffset={SIDE_OFFSET}
                >
                    {content}
                    <RadixTooltip.Arrow className="bilda-tooltip__arrow" />
                </RadixTooltip.Content>
            </RadixTooltip.Portal>
        </RadixTooltip.Root>
    ),
);
Tooltip.displayName = "Tooltip";

export type TooltipProviderProps = {
    readonly children: ReactNode;
    // Shared open-delay for every tooltip under this provider. Mirrors the
    // per-tooltip default so a lone <Tooltip> and a provider-wrapped tree
    // feel the same.
    readonly delayDuration?: number;
};

// Mount once at the app root. Radix uses it to skip the delay when the
// pointer moves directly from one tooltip's trigger to another's.
export const TooltipProvider = ({
    children,
    delayDuration = 300,
}: TooltipProviderProps) => (
    <RadixTooltip.Provider delayDuration={delayDuration}>
        {children}
    </RadixTooltip.Provider>
);
